/* ============================================================================
   FIRST-ADD PANEL — behaviour for snippets/sb-first-add.liquid

   The sheet that comes down the first time a shopper puts something into an
   empty cart. Read the snippet's header comment first: it carries the trigger
   rules, the discount facts this file spends, and the reason every title and
   every price arrives from Liquid rather than being assembled here.

   Three things happen in this file.

   1. CATCHING THE ADD. It subscribes to Dawn's cartUpdate. Both PDP add paths
      publish under source 'product-form' — Dawn's <product-form> and the PDP
      routine builder, which impersonates it deliberately (sb-product.js). The
      range-card quick-add publishes 'sb-quick-add' and is ignored so that
      button stays silent, and the cart drawer's own upsell publishes
      'product-form' too, so an open drawer is checked for separately.

   2. READING THE CART AS IT WAS. Nothing is asked of the network. The header
      bubble still carries its pre-add, protection-adjusted state at the
      moment subscribers run, because every add path publishes BEFORE it
      re-renders sections: product-form.js publishes at :71 and calls
      renderContents at :95; the routine builder publishes at :790 and calls
      renderRoutineCart at :797.

   3. STANDING THE DRAWER DOWN. CartDrawer.open is wrapped once with a
      one-shot suppressor rather than forking every add path. The drawer still
      re-renders — which matters, because that re-render is what connects
      <secure-checkout> and lets shipment protection add itself — it simply
      does not come out. renderContents IS wrapped too, and its `shouldOpen`
      argument must be forwarded: sb-quick-add.js passes false to refresh the
      drawer without opening it, and a wrapper that drops it turns the range
      card's deliberately silent add into one that flings the drawer open.
   ============================================================================ */

(function () {
  'use strict';

  if (window.SBFirstAddBound) return;
  window.SBFirstAddBound = true;

  /* One-shot suppressor for CartDrawer.open(). It disarms itself on a timer as
     well as on use, because the add paths do not all reach open(): if
     product-form.js finds no cart element it redirects instead, and a failed
     /cart/change.js never renders. An armed flag left behind would swallow the
     shopper's NEXT deliberate click on the cart icon, which is a far worse bug
     than the drawer flashing open once. */
  var SUPPRESS = {
    armed: false,
    timer: 0,
    arm: function () {
      this.armed = true;
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(this.disarm.bind(this), 1500);
    },
    disarm: function () {
      this.armed = false;
      window.clearTimeout(this.timer);
    },
    consume: function () {
      if (!this.armed) return false;
      this.disarm();
      return true;
    },
  };

  /* Cart line images arrive as protocol-relative CDN URLs. Only used as a
     fallback: a product carried by the Liquid island brings its own, already
     sized and already routed through the image-override chain. */
  function sized(url, width) {
    if (!url) return '';
    var absolute = url.indexOf('//') === 0 ? window.location.protocol + url : url;
    return absolute + (absolute.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ONCE, EVER — the sheet is a greeting, and a greeting repeated is a
     nag. It is spent the first time it is shown and never comes back on this
     browser, so the shopper who adds a second jar a week later gets the cart
     drawer like everyone else.

     Storage is wrapped because Safari's Private Browsing throws rather than
     returning null, and a throw here must not take the add down with it.
     Neither answer is allowed to be an exception: an unreadable store reads
     as "not yet seen" and an unwritable one is simply not written. The
     in-memory flag is set either way, so even with storage refusing entirely
     the sheet still comes down at most once per page. */
  var SEEN_KEY = 'sb:first-add:seen';
  var seenInMemory = false;

  function hasBeenSeen() {
    if (seenInMemory) return true;
    try {
      return window.localStorage.getItem(SEEN_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function markSeen() {
    seenInMemory = true;
    try {
      window.localStorage.setItem(SEEN_KEY, '1');
    } catch (error) {
      /* Private mode, or storage full. The in-memory flag still holds for
         this page, and the sheet showing once more on the next page load is
         a far smaller failure than an add that never completes. */
    }
  }

  /* THE WAY BACK IN. Once-ever is right for a shopper and impossible for
     anyone testing the store: the first add spends the flag, and every add
     after it looks like a feature that has stopped working. So the flag can
     be re-armed without devtools —

       ?sb-first-add=reset   on any URL, or
       sbFirstAdd.reset()    in the console

     — and `sbFirstAdd.seen()` answers whether it is currently spent. The
     query form is read from search AND hash so it survives being pasted onto
     a URL that already carries Shopify's own parameters. Nothing here fires
     on its own: the flag is never cleared for a shopper who did not ask. */
  function forget() {
    seenInMemory = false;
    try {
      window.localStorage.removeItem(SEEN_KEY);
    } catch (error) {
      /* Nothing to do — the in-memory flag is already cleared. */
    }
  }

  if (/[?&#]sb-first-add=reset(&|$)/.test(window.location.search + window.location.hash)) {
    forget();
  }

  window.sbFirstAdd = { reset: forget, seen: hasBeenSeen };

  function fill(text, replacements) {
    var out = text || '';
    Object.keys(replacements || {}).forEach(function (name) {
      out = out.split('[' + name + ']').join(replacements[name]);
    });
    return out;
  }

  /* There is deliberately NO money formatter in this file.

     Every price the sheet shows was formatted by Liquid's `| money`, which is
     presentment-aware — this storefront sells in seven currencies
     (CZK/DKK/EUR/HUF/PLN/RON/SEK) while shop.money_format is euros only, so a
     JS formatter fed that format would print € over złoty amounts. The sheet
     therefore never composes a price; it only ever repeats one Liquid already
     wrote, and prints it only after checking the cart agrees to the cent. */
  function centsOf(money) {
    var digits = String(money || '').replace(/[^\d]/g, '');
    return digits ? Number(digits) : null;
  }

  if (!customElements.get('sb-first-add')) {
    customElements.define(
      'sb-first-add',
      class SbFirstAdd extends HTMLElement {
        connectedCallback() {
          if (this.bound) return;
          this.bound = true;

          this.offers = window.sbFirstAddOffers || {};
          this.strings = window.sbFirstAddStrings || {};

          this.sheet = this.querySelector('[data-fa-sheet]');
          this.offer = this.querySelector('[data-fa-offer]');
          this.done = this.querySelector('[data-fa-done]');
          this.foot = this.querySelector('[data-fa-foot]');
          this.closeButton = this.querySelector('[data-fa-close]');
          this.addButton = this.querySelector('[data-fa-add]');
          this.errorEl = this.querySelector('[data-fa-error]');

          this.querySelector('[data-fa-scrim]').addEventListener('click', this.close.bind(this));
          this.closeButton.addEventListener('click', this.close.bind(this));
          this.addButton.addEventListener('click', this.addSecond.bind(this));
          this.querySelector('[data-fa-dismiss]').addEventListener('click', this.close.bind(this));
          this.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') this.close();
          });

          this.standDrawerDown();

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
          }
        }

        standDrawerDown() {
          var drawer = customElements.get('cart-drawer');
          if (!drawer || drawer.prototype.__sbFirstAddPatched) return;
          var open = drawer.prototype.open;
          drawer.prototype.open = function (triggeredBy) {
            if (SUPPRESS.consume()) return;
            return open.call(this, triggeredBy);
          };

          /* HELD, NOT SKIPPED — the second half of standing the drawer down.
             product-form.js calls renderContents() the instant we return from
             onCartUpdate: it DOMParser-parses every drawer section, re-inserts
             their <style> blocks into the live document and swaps the drawer's
             markup wholesale. On a FIRST add that is the expensive version of
             the job — the cart was empty, so <secure-checkout> is absent from
             the server-rendered drawer, connects for the first time in this
             render, and fires a request and a second swap of its own.

             All of it lands AFTER `is-open` has armed the sheet's transitions,
             re-dirtying the whole document in the very rendering update that
             has to create them. That is the one thing open() spends its entire
             preamble avoiding, and it cannot be avoided from inside open()
             because it happens after open() has returned.

             So the render is held for 400ms: inside the sheet's travel, and
             long before the drawer can be reached behind an inert page. The
             basket is still made right — it is simply not made right in the
             task that has to start a 0.9s animation. Held only while the
             suppressor is armed, so every ordinary add passes straight
             through untouched.

             SUPPRESS is re-armed around the deferred call because
             renderContents ends by calling open() on a timer of its own. Fire
             it unarmed and the drawer flings itself open 400ms after the
             shopper was deliberately shown this sheet instead. */
          var render = drawer.prototype.renderContents;
          drawer.prototype.renderContents = function (parsedState, shouldOpen) {
            if (!SUPPRESS.armed) {
              // A newer cart supersedes anything still being held. Drop the
              // held state rather than letting it land on top of this one
              // 400ms from now and walk the basket backwards.
              window.clearTimeout(this.__sbFaRenderTimer);
              this.__sbFaPending = null;
              this.__sbFaPendingOpen = undefined;
              return render.call(this, parsedState, shouldOpen);
            }
            var drawerEl = this;
            window.clearTimeout(drawerEl.__sbFaRenderTimer);
            drawerEl.__sbFaPending = parsedState;
            drawerEl.__sbFaPendingOpen = shouldOpen;
            drawerEl.__sbFaRenderTimer = window.setTimeout(function () {
              drawerEl.__sbFaFlush();
            }, 400);
          };

          drawer.prototype.__sbFaFlush = function () {
            window.clearTimeout(this.__sbFaRenderTimer);
            var pending = this.__sbFaPending;
            if (!pending) return;
            var pendingOpen = this.__sbFaPendingOpen;
            this.__sbFaPending = null;
            this.__sbFaPendingOpen = undefined;
            // Only re-arm for a render that is going to open. A caller that
            // asked for shouldOpen: false never reaches open(), so arming for
            // it would leave the flag set to swallow the shopper's next
            // deliberate click on the cart icon instead.
            if (pendingOpen !== false) SUPPRESS.arm();
            render.call(this, pending, pendingOpen);
          };

          drawer.prototype.__sbFirstAddPatched = true;
        }

        /* The header bubble carries `site-actions__cart--empty` whenever the
           cart holds no formulas. Both header renderers subtract the
           shipment-protection quantity before stamping it, so the €1,89 line
           that adds itself never masks an otherwise empty cart. */
        cartWasEmpty() {
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble) return bubble.classList.contains('site-actions__cart--empty');
          var drawer = document.querySelector('cart-drawer');
          return !!drawer && drawer.classList.contains('is-empty');
        }

        onCartUpdate(event) {
          if (!event || event.source !== 'product-form' || !event.cartData) return;
          if (document.body.classList.contains('template-cart')) return;

          // The drawer's own upsell publishes under this source as well. If
          // the drawer is already open, the shopper is looking at the cart and
          // does not need a second cart surface landing on top of it.
          var drawer = document.querySelector('cart-drawer');
          if (drawer && drawer.classList.contains('active')) return;

          if (!this.cartWasEmpty()) return;

          var added = this.addedLines(event.cartData);
          if (!added.length) return;

          /* ONE JAR, ONCE. The sheet exists to offer the second jar, so it
             only stands where that offer is real: a single jar of a single
             discountable formula, bought once. Every other add — two or three
             jars, a subscription, a routine-builder basket, a formula with no
             two-month discount behind it — has nothing to say that the cart
             drawer does not say better, and gets the drawer.

             Checked BEFORE the drawer is stood down: a shopper who is not
             getting the sheet must still get their cart. */
          var line = added[0];
          var offer = this.offers[String(line.product_id)] || null;
          if (!this.qualifies(line, offer, added.length)) return;

          if (hasBeenSeen()) return;

          SUPPRESS.arm();
          this.present(line, added.length, offer);
          // Spent only once the sheet is actually up. Marking it first would
          // mean a throw anywhere in present() burns the shopper's one
          // showing on a sheet they never saw — and burns it permanently.
          markSeen();
        }

        /* The offer stands only where −15% is genuinely what happens next:
           one jar, bought once, of a formula the store discounts at two, and
           nothing else riding in on the same request. It is now the condition
           for the whole sheet, not just its lower register — see onCartUpdate. */
        qualifies(line, offer, addedCount) {
          return (
            !!offer &&
            addedCount === 1 &&
            !line.selling_plan_allocation &&
            Number(line.quantity) === 1
          );
        }

        /* The add response is either a single line item (Dawn's FormData
           post) or {items: [...]} (the routine builder's JSON post, which has
           no top-level key). The routine builder can put two or three
           formulas in with one request, so the COUNT matters as much as the
           first line: an offer priced as if the cart held one formula would
           be wrong about a cart that holds three. */
        addedLines(data) {
          var items = Array.isArray(data.items) ? data.items : data.key ? [data] : [];
          return items.filter(function (item) {
            return item && item.requires_shipping !== false;
          });
        }

        present(line, addedCount, offer) {
          this.line = line;
          this.opener = document.activeElement;
          this.errorEl.hidden = true;

          var image = line.image || line.featured_image;
          if (image && typeof image === 'object') image = image.url;

          var thumb = this.querySelector('[data-fa-thumb]');
          thumb.src = offer ? offer.thumb : sized(image, 220);
          thumb.alt = '';

          var title = offer ? offer.title : line.product_title || line.title || '';
          this.querySelector('[data-fa-title]').textContent = title;

          // The meta line names the purchase the way the shopper chose it: the
          // plan's own name when there is one, the unit price when there is
          // not. No price is invented for a subscription line here — the PDP
          // has just shown it, and the plan name is what changed.
          var plan =
            line.selling_plan_allocation &&
            line.selling_plan_allocation.selling_plan &&
            line.selling_plan_allocation.selling_plan.name;
          var bits = [];
          if (plan) bits.push(plan);
          if (line.quantity > 1) bits.push(line.quantity + ' ×');
          if (!plan && offer) bits.push(offer.unit);
          this.querySelector('[data-fa-meta]').textContent = bits.join(' · ');

          this.buildOffer(line, offer, addedCount);
          this.open();
        }

        /* The register is still built defensively rather than assumed open:
           the sheet is only ever presented for a qualifying line today, but
           the gate lives in one place and this keeps the two in step if the
           trigger is ever widened again. */
        buildOffer(line, offer, addedCount) {
          var eligible = this.qualifies(line, offer, addedCount);

          this.offer.hidden = !eligible;
          this.foot.hidden = !eligible;
          this.addButton.hidden = !eligible;
          this.done.hidden = true;
          this.done.removeAttribute('tabindex');
          if (!eligible) return;

          this.querySelector('[data-fa-offer-title]').textContent = fill(this.strings.offer_title, {
            price: offer.each,
          });
          this.querySelector('[data-fa-was]').textContent = offer.was;
          this.querySelector('[data-fa-now]').textContent = offer.now;
          this.querySelector('[data-fa-save]').textContent = fill(this.strings.save, {
            amount: offer.save,
          });
          this.querySelector('[data-fa-cta-price]').textContent = offer.now;

          var front = this.querySelector('[data-fa-pair-front]');
          var back = this.querySelector('[data-fa-pair-back]');
          front.src = offer.shot;
          back.src = offer.shot;

          this.projected = offer;
        }

        /* ---- Opening and closing ------------------------------------------- */

        open() {
          // A close() still in its exit timer would otherwise hide the sheet
          // again partway through this opening.
          window.clearTimeout(this.exitTimer);

          /* ORDER IS LOAD-BEARING FROM HERE DOWN TO `is-open`. Everything that
             dirties anything OUTSIDE this element is done FIRST and flushed
             once, so the rendering update that has to CREATE the sheet's, the
             scrim's and the two registers' transitions has only this panel's
             own nodes left to restyle.

             What goes wrong otherwise is not that the animation is slow — it
             is that its SECOND frame is already a quarter of the way down. A
             transition starts at zero on the frame it is created; if that same
             frame must also restyle the whole document it is presented late,
             and the next frame lands 40ms+ into a curve that is 20% travelled
             by 30ms. The eye reads that jump, not the travel, and the only
             part of the movement left to watch is the slow tail.

             `inert` is the expensive half: setModalIsolation puts it on <main>
             and it is an INHERITED computed flag, so the whole PDP subtree is
             restyled. overflow-hidden is near-free under macOS overlay
             scrollbars — it costs only on a Mac set to always show them.

             The third source of that restyle is not ours and cannot be reached
             from here: product-form.js re-renders the entire cart drawer
             synchronously the moment we return. See standDrawerDown(). */
          document.body.classList.add('overflow-hidden');
          setModalIsolation(this, true);

          this.hidden = false;

          // ONE forced style + layout, covering the hidden→visible commit AND
          // both invalidations above. Nothing below this line may dirty
          // anything outside this element.
          void this.offsetWidth;

          this.classList.add('is-open');

          /* The blur is hung on one frame later, on purpose — see the long
             note in sb-first-add.css. An element that was display:none when
             its layer was built can be composited with no backdrop root, and
             a radius applied to it then has nothing to filter: the page goes
             tinted but perfectly sharp, which is what the owner was seeing.
             Asking for the filter when the layer is already on screen is what
             makes WebKit go and build one. */
          window.requestAnimationFrame(() => {
            if (!this.classList.contains('is-open')) return;
            this.classList.add('is-blurred');
            /* One more frame before the players are touched. Frame 1 starts
               the sheet travelling, frame 2 builds the backdrop root, frame 3
               parks the media — pause() reconfigures a media layer, and it
               must not land on either of the first two. */
            window.requestAnimationFrame(() => {
              if (this.classList.contains('is-open')) this.holdMedia();
            });
          });

          // Focus is taken once the sheet has arrived. setModalIsolation has
          // inerted the subtree holding the button that was pressed, so focus
          // is already on <body> and Escape does not reach this element until
          // the trap lands — the gap must be as short as the arrival allows.
          // Under reduced motion there is no arrival and transitionend NEVER
          // fires, so the timer is the only path and it runs immediately.
          var trap = () => {
            if (this.trapped || !this.classList.contains('is-open')) return;
            this.trapped = true;
            trapFocus(this.sheet, this.closeButton);
          };
          this.trapped = false;
          var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.sheet.addEventListener('transitionend', trap, { once: true });
          this.trapTimer = window.setTimeout(trap, still ? 0 : 1000);
        }

        close() {
          if (!this.classList.contains('is-open')) return;
          window.clearTimeout(this.trapTimer);
          this.trapped = false;

          /* The same order as open(), inverted, and for the same reason: the
             teardown dirties the whole document — un-inerting <main> restyles
             that subtree all over again — so it is done and FLUSHED before the
             class that starts the exit. Left where it used to be, the sheet
             jumped a quarter of the way back up on the exit's second frame
             exactly as it did on the entrance, and the exit is the barer of
             the two: there is no network wait in front of it to hide a stall.

             The page becomes reachable one frame earlier than before as a
             result, which is invisible — the sheet is still covering it, and
             .sb-fa only carries pointer-events while .is-open is on. Focus is
             handed back AFTER the un-inert and never before: the opener lives
             inside <main> and is not focusable while <main> is inert. */
          document.body.classList.remove('overflow-hidden');
          setModalIsolation(this, false);

          // Any drawer render standDrawerDown() is still holding is made good
          // here rather than on its own timer: the page has just become
          // reachable again, and a shopper who dismissed the sheet inside that
          // 400ms must not be able to reach a stale basket. Paid before the
          // flush below, like everything else in this block.
          var drawerEl = document.querySelector('cart-drawer');
          if (drawerEl && drawerEl.__sbFaFlush) drawerEl.__sbFaFlush();

          removeTrapFocus(this.opener instanceof HTMLElement ? this.opener : null);

          void this.offsetWidth;

          this.classList.remove('is-open');
          // The blur goes with the tint rather than lingering over a page the
          // sheet has already left; the tint's own fade covers the cut.
          this.classList.remove('is-blurred');

          // Stay in the DOM until the sheet has finished travelling back up,
          // then leave the layer entirely. The page is let move again at the
          // same moment rather than in close(): released any earlier and the
          // films would be repainting under a blur that is still on screen,
          // which is the exact cost holdMedia() exists to avoid — and the page
          // coming back to life is a thing to watch AFTER the sheet has gone,
          // not something revealed through it on the way out.
          this.exitTimer = window.setTimeout(() => {
            if (this.classList.contains('is-open')) return;
            this.hidden = true;
            this.releaseMedia();
          }, 950);
        }

        /* ---- Holding the page still --------------------------------------
           A backdrop-filter is not a snapshot: it is re-derived whenever
           anything painted beneath it changes. One looping film under this
           sheet therefore buys a full-viewport blur pass per video frame — for
           a picture the scrim has already dimmed to almost nothing, for as
           long as the panel is up. This is the difference between the
           compositor going idle behind the sheet and it working at the film's
           frame rate for something nobody can see. Every other modal on this
           store quiets its page; this one did not.

           HELD, NOT STOPPED. Only what was actually playing is remembered and
           only that is restarted, so a film the shopper paused themselves
           stays paused. Deliberately NOT global.js's pauseAllMedia(): that one
           also postMessages YouTube and Vimeo iframes and has no restore path
           at all — fine for the cart drawer, which is a departure, and wrong
           here, where the shopper is handed straight back to the page they
           were reading. */
        holdMedia() {
          if (this.held) return;
          var panel = this;
          var held = [];
          var hold = function (media) {
            if (panel.contains(media)) return;
            if (media.paused || media.ended || !media.currentSrc) return;
            media.pause();
            if (held.indexOf(media) === -1) held.push(media);
          };
          document.querySelectorAll('video, audio').forEach(hold);
          this.held = held;

          /* The net underneath the page's own observers. Locking the body
             re-fires every IntersectionObserver on the document, and several
             sections answer an intersection by calling play() — sb-fixes.js,
             sb-timeline.js and sb-product.js all do. Those would start films
             that were NOT running when the hold was taken, which is precisely
             the case this exists for. `play` does not bubble, hence capture. */
          this.onStrayPlay = function (event) {
            hold(event.target);
          };
          document.addEventListener('play', this.onStrayPlay, true);
        }

        releaseMedia() {
          if (this.onStrayPlay) {
            document.removeEventListener('play', this.onStrayPlay, true);
            this.onStrayPlay = null;
          }
          (this.held || []).forEach(function (media) {
            // A section re-render between the hold and the release can swap
            // the element out from under us.
            if (!media.isConnected) return;
            var started = media.play();
            if (started && started.catch) started.catch(function () {});
          });
          this.held = null;
        }

        /* ---- Taking the offer ------------------------------------------------
           /cart/change.js answers with the whole cart, so what is reported
           back is the discount Shopify actually applied, not the projection. */

        addSecond() {
          if (this.addButton.getAttribute('aria-disabled') === 'true') return;
          this.addButton.setAttribute('aria-disabled', 'true');
          this.addButton.classList.add('loading');
          this.errorEl.hidden = true;
          var spinner = this.addButton.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');

          var drawer = document.querySelector('cart-drawer');
          var sections = drawer
            ? drawer.getSectionsToRender().map(function (section) { return section.id; })
            : [];

          fetch(window.routes.cart_change_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              id: this.line.key,
              quantity: 2,
              sections: sections,
              sections_url: window.location.pathname,
            }),
          })
            .then(function (response) { return response.json(); })
            .then((cart) => {
              if (cart.status) {
                var refused = new Error(cart.description || cart.message);
                refused.fromCart = true;
                throw refused;
              }
              this.reportApplied(cart);
              if (drawer && cart.sections) {
                // Bring the drawer up to date underneath without letting it
                // out, so the basket is already right when it is opened.
                SUPPRESS.arm();
                drawer.renderContents(cart);
              }
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'sb-first-add',
                  productVariantId: String(this.line.id),
                  cartData: cart,
                });
              }
            })
            .catch((error) => {
              // The localized string leads. error.message is only ever shown
              // when it came from the parsed cart response — a raw
              // "Failed to fetch" is the browser's English, not ours, and it
              // would otherwise win here on every dropped connection.
              this.errorEl.textContent = error.fromCart ? error.message : this.strings.error;
              this.errorEl.hidden = false;
            })
            .finally(() => {
              this.addButton.removeAttribute('aria-disabled');
              this.addButton.classList.remove('loading');
              if (spinner) spinner.classList.add('hidden');
            });
        }

        /* The offer is over; what is printed now is a statement about what the
           cart DID, so nothing here may be projected.

           /cart/change.js answers 200 and silently clamps when stock is short,
           and a discount can be paused in Admin between the sheet quoting it
           and the shopper pressing the button. So the line quantity is read
           back rather than assumed, and the saving is spoken only when the
           cart's own delta matches the quoted figure to the cent. When it does
           not — clamped, paused, or a multi-line cart whose delta is not this
           click's — the sheet confirms the add and says nothing about money.
           It never restates the projection as something that happened. */
        reportApplied(cart) {
          this.offer.hidden = true;
          this.foot.hidden = true;
          this.addButton.hidden = true;
          this.done.hidden = false;

          var items = Array.isArray(cart.items) ? cart.items : [];
          var line = null;
          for (var i = 0; i < items.length; i += 1) {
            if (items[i] && items[i].key === this.line.key) {
              line = items[i];
              break;
            }
          }
          // Applying the pair discount changes Shopify's line key. Recover
          // the same one-time variant and properties, only if unambiguous.
          if (!line) {
            var properties = JSON.stringify(Object.entries(this.line.properties || {}).sort());
            var matches = items.filter((item) => {
              return item &&
                String(item.variant_id) === String(this.line.variant_id || this.line.id) &&
                !item.selling_plan_allocation &&
                JSON.stringify(Object.entries(item.properties || {}).sort()) === properties;
            });
            if (matches.length === 1) line = matches[0];
          }
          var quantity = line ? Number(line.quantity) : 0;

          var applied = Number(cart.original_total_price) - Number(cart.total_price);
          var quoted = centsOf(this.projected.save);
          var told = quantity === 2 && isFinite(applied) && quoted !== null && applied === quoted;

          // The receipt above still reads as one jar at one jar's price.
          this.querySelector('[data-fa-meta]').textContent = told
            ? ['2 ×', this.projected.now].join(' · ')
            : quantity + ' ×';

          this.querySelector('[data-fa-done-line]').textContent = told
            ? fill(this.strings.applied, { amount: this.projected.save })
            : this.strings.added;

          // The pressed button is about to leave the layout, which would drop
          // focus to <body> — outside the dialog, where Escape no longer
          // reaches the handler. Hand it to the line that replaced it.
          this.done.setAttribute('tabindex', '-1');
          this.done.focus();
        }
      }
    );
  }
})();
